import { motion } from "framer-motion";
import {
  MessageCircle,
  Lock,
  CheckCircle2,
  Clock,
  Loader2,
  FolderOpen,
  CalendarClock,
} from "lucide-react";
import { List, Badge, Avatar, springs } from "@/components/ui";
import { TagBadge } from "@/components/ui/Tag/TagBadge";
import { useTranslation } from "@audit-app/utils";
import { FileIcon } from "./FileIcon";
import { getCategoryLucideIcon } from "./category-icon-map";
import { statusToBadge, statusLabel } from "./constants";
import type { DocumentView } from "./types";
import { DocumentRowMenu } from "./DocumentRowMenu";

interface DocumentListViewProps {
  groupedDocuments?: Map<string, DocumentView[]> | null;
  documents?: DocumentView[];
  onRowClick: (id: string) => void;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onTagClick?: (docId: string) => void;
  onDownload?: (docId: string) => void;
  onDelete?: (docId: string) => void;
}

function ChecklistMini({ doc }: { doc: DocumentView }) {
  const checklist = doc.checklist;
  if (!checklist || checklist.length === 0) return null;

  const done = checklist.filter((c) => c.done).length;
  const total = checklist.length;
  const progress = (done / total) * 100;

  return (
    <div className="flex items-center gap-1.5 w-24">
      <CheckCircle2 size={12} className="text-muted shrink-0" />
      <div className="flex-1 h-1 bg-border/50 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ ...springs.smooth, delay: 0.4 }}
          style={{
            backgroundColor:
              progress === 100
                ? "#22c55e"
                : progress >= 50
                  ? "#E8862A"
                  : "#a1a1a6",
          }}
        />
      </div>
      <span className="text-[10px] text-muted font-medium shrink-0">
        {done}/{total}
      </span>
    </div>
  );
}

function ExpirationIndicator({
  timestamp,
  t,
}: {
  timestamp: number;
  t: import("i18next").TFunction<["documents"]>;
}) {
  const now = Date.now();
  const daysUntil = Math.ceil((timestamp - now) / (1000 * 60 * 60 * 24));

  let colorClass: string;
  if (daysUntil <= 0) {
    colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
  } else if (daysUntil <= 7) {
    colorClass = "text-red-400 bg-red-500/10 border-red-500/20";
  } else if (daysUntil <= 30) {
    colorClass = "text-amber-400 bg-amber-500/10 border-amber-500/20";
  } else {
    colorClass = "text-muted bg-card border-border/50";
  }

  const label =
    daysUntil <= 0
      ? t("documents:documentGridCard.expired")
      : daysUntil === 1
        ? t("documents:documentGridCard.dayLeft")
        : t("documents:documentGridCard.daysLeft", { days: daysUntil });

  return (
    <span
      className={`hidden sm:inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium border ${colorClass}`}
    >
      <CalendarClock size={10} className="shrink-0" />
      {label}
    </span>
  );
}

interface DocumentRowContentProps {
  doc: DocumentView;
  index: number;
  onTagClick?: (docId: string) => void;
  onDownload?: (docId: string) => void;
  onDelete?: (docId: string) => void;
}

function DocumentRowContent({
  doc,
  index,
  onTagClick,
  onDownload,
  onDelete,
}: DocumentRowContentProps) {
  const { t } = useTranslation(["documents"]);
  const author = doc.author ?? {
    name: t("documents:documentGridCard.unknown"),
  };
  const isGenerating =
    doc.isProcessing || doc.aiSummary?.status === "generating";
  const hasActions = onDownload || onDelete || onTagClick;

  const descriptionText = isGenerating
    ? "Generating summary\u2026"
    : (doc.description ?? doc.aiSummary?.text);

  return (
    <motion.div
      className="flex items-center gap-3 w-full"
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        ...springs.snappy,
        delay: Math.min(index, 16) * 0.03,
      }}
    >
      {/* File icon */}
      <div className="shrink-0">
        <FileIcon type={doc.type} />
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0">
        {/* Line 1: title + status + processing + tags + checklist */}
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-semibold text-primary truncate shrink min-w-0">
            {doc.title}
          </p>

          <div className="flex items-center gap-1.5 shrink-0">
            <Badge variant="status" status={statusToBadge[doc.status]}>
              {doc.status === "under-review" && (
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-current mr-1 align-middle"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{
                    duration: 2,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  }}
                />
              )}
              {statusLabel[doc.status]}
            </Badge>

            {isGenerating && (
              <Loader2
                size={12}
                className="animate-spin text-accent shrink-0"
              />
            )}

            {doc.expirationDateTimestamp && (
              <ExpirationIndicator
                timestamp={doc.expirationDateTimestamp}
                t={t}
              />
            )}
          </div>

          {/* Tags — visible from sm+ */}
          {doc.tags && doc.tags.length > 0 && (
            <div className="hidden sm:flex items-center gap-1 shrink-0">
              {doc.tags.slice(0, 2).map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color}
                  onClick={() => onTagClick?.(doc.id)}
                />
              ))}
              {doc.tags.length > 2 && (
                <span className="text-[10px] text-muted font-medium">
                  +{doc.tags.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Checklist mini — visible from md+ */}
          <div className="hidden md:block shrink-0">
            <ChecklistMini doc={doc} />
          </div>
        </div>

        {/* Line 2: metadata + desktop extras */}
        <div className="flex items-center gap-2 mt-0.5 min-w-0">
          <span className="flex items-center gap-1 text-[11px] text-muted shrink-0">
            <Clock size={10} className="shrink-0" />
            {doc.updatedAt}
          </span>
          <span className="text-[11px] text-muted shrink-0">{doc.size}</span>

          {/* Desktop secondary info — md+ */}
          {descriptionText && (
            <p
              className={`hidden md:block text-[11px] text-secondary truncate min-w-0 ${
                isGenerating ? "italic text-muted" : ""
              }`}
            >
              {isGenerating && (
                <motion.span
                  className="inline-block"
                  animate={{ opacity: [1, 0.4, 1] }}
                  transition={{
                    duration: 1.5,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "easeInOut",
                  }}
                >
                  {descriptionText}
                </motion.span>
              )}
              {!isGenerating && descriptionText}
            </p>
          )}

          {doc.confidential && (
            <Lock size={12} className="hidden md:block text-muted shrink-0" />
          )}

          {doc.commentCount != null && doc.commentCount > 0 && (
            <span className="hidden md:flex items-center gap-1 text-[11px] text-secondary shrink-0">
              <MessageCircle size={12} className="shrink-0" />
              {doc.commentCount}
            </span>
          )}

          {/* Author — md+ */}
          <div className="hidden md:flex items-center gap-1.5 ml-auto shrink-0">
            <Avatar
              name={author.name}
              avatarColor={author.avatarColor}
              avatarUrl={author.avatarUrl}
              size="xs"
            />
            <span className="text-[11px] text-muted">
              {author.name.split(" ")[0]}
            </span>
          </div>
        </div>
      </div>

      {/* Actions menu — always visible when callbacks exist */}
      {hasActions && (
        <div className="shrink-0">
          <DocumentRowMenu
            documentId={doc.id}
            documentTitle={doc.title}
            onDownload={() => onDownload?.(doc.id)}
            onDelete={() => onDelete?.(doc.id)}
            onEditTags={() => onTagClick?.(doc.id)}
          />
        </div>
      )}
    </motion.div>
  );
}

export function DocumentListView({
  groupedDocuments,
  documents,
  onRowClick,
  onSelectionChange,
  onTagClick,
  onDownload,
  onDelete,
}: DocumentListViewProps) {
  // Render grouped mode if groupedDocuments is provided and not null
  const isGroupedMode = groupedDocuments != null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-y-auto"
    >
      <List.Provider
        onRowClick={onRowClick}
        onSelectionChange={onSelectionChange}
      >
        <List>
          {isGroupedMode
            ? // Grouped mode: render documents organized by category
              Array.from(groupedDocuments.entries()).map(
                ([categoryData, docs]) => {
                  // categoryData format: "iconName|categoryName" or just "Uncategorized"
                  const [iconName, categoryName] = categoryData.includes("|")
                    ? categoryData.split("|")
                    : ["folder", categoryData];
                  const IconComponent =
                    getCategoryLucideIcon(iconName ?? "folder") ?? FolderOpen;

                  return (
                    <List.Group
                      key={categoryData}
                      title={categoryName ?? categoryData}
                      icon={<IconComponent className="h-3.5 w-3.5" />}
                      count={docs.length}
                    >
                      {docs.map((doc, i) => (
                        <List.Row key={doc.id} id={doc.id}>
                          <DocumentRowContent
                            doc={doc}
                            index={i}
                            onTagClick={onTagClick}
                            onDownload={onDownload}
                            onDelete={onDelete}
                          />
                        </List.Row>
                      ))}
                    </List.Group>
                  );
                },
              )
            : // Ungrouped mode: render flat list of documents
              documents?.map((doc, i) => (
                <List.Row key={doc.id} id={doc.id}>
                  <DocumentRowContent
                    doc={doc}
                    index={i}
                    onTagClick={onTagClick}
                    onDownload={onDownload}
                    onDelete={onDelete}
                  />
                </List.Row>
              ))}
        </List>
      </List.Provider>
    </motion.div>
  );
}
